import React, { useState } from 'react';
import { MessageCircle, X } from 'lucide-react';
import { useT } from '../../i18n';
import { TEAM_AVATARS } from '../../components/teamAvatars';
import { useOs2, PageHead, Card, fmtDateTime, timeAgo, Fade, LoadingState } from './os2ui';

/* AI Team — alive. "Is every AI agent working, and what is each one doing right now?" */

const SCHEDULE = {
  eva: ['Daily executive briefing · 8:00 AM', 'Briefing ejecutivo diario · 8:00 AM'],
  penny: ['Monday digest + article publishing', 'Digest de los lunes + publicación de artículos'],
  sonny: ['Social packages on every article publish', 'Packs sociales con cada artículo publicado'],
  rachel: ['24/7 on WhatsApp & web concierge', '24/7 en WhatsApp y concierge web'],
  stan: ['Continuous prospecting & partner care', 'Prospección continua y cuidado de partners'],
  linda: ['On demand — docs & knowledge base', 'Bajo demanda — documentos y knowledge base'],
};

const friendly = (type) => (type || '').replace(/_/g, ' ').toLowerCase();

function ConversationModal({ agent, avatar, isEs, onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="agent-conversation-modal">
      <div className="absolute inset-0 bg-gray-900/30 backdrop-blur-[2px]" onClick={onClose} />
      <div className="relative w-full max-w-md max-h-[80vh] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden">
        <div className="flex items-center gap-3 px-5 py-4 border-b border-gray-100 bg-gray-50/60">
          <img src={avatar} alt={agent.name} className="w-10 h-10 rounded-xl object-cover" />
          <div className="flex-1">
            <p className="text-[15px] font-semibold tracking-tight">{agent.name}</p>
            <p className="text-[11px] text-emerald-600 font-medium flex items-center gap-1">
              <span className="relative flex w-2 h-2"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" /><span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" /></span>
              {agent.role}
            </p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100 text-gray-400" data-testid="agent-conversation-close"><X className="w-4 h-4" /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-5 space-y-3 bg-[#FAFAFA]">
          {(agent.timeline || []).slice().reverse().map((e, i) => (
            <div key={i} className="flex items-end gap-2">
              <img src={avatar} alt="" className="w-6 h-6 rounded-lg object-cover mb-0.5" />
              <div className="max-w-[85%] rounded-2xl rounded-bl-sm bg-white border border-gray-200 px-3.5 py-2.5 shadow-[0_1px_2px_rgba(0,0,0,0.04)]">
                <p className="text-[13px] text-gray-800 capitalize">{friendly(e.type)}{e.detail ? ` — ${e.detail}` : ''}</p>
                <p className="text-[10px] text-gray-300 mt-0.5">{fmtDateTime(e.at)}</p>
              </div>
            </div>
          ))}
          {!agent.timeline?.length && <p className="text-[13px] text-gray-400 text-center py-6">{isEs ? 'Sin actividad registrada todavía.' : 'No recorded activity yet.'}</p>}
        </div>
        <div className="px-5 py-3 border-t border-gray-100 bg-white">
          {agent.key === 'rachel' ? (
            <a href="https://wa.me/14085901102" target="_blank" rel="noreferrer"
              className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white text-[13px] font-semibold px-4 py-2.5 transition-colors"
              data-testid="agent-whatsapp-btn">
              <MessageCircle className="w-4 h-4" /> {isEs ? 'Hablar con Rachel por WhatsApp' : 'Chat with Rachel on WhatsApp'}
            </a>
          ) : (
            <p className="text-[11.5px] text-gray-400 text-center">{isEs ? 'Chat directo con agentes — próximamente. Esta es su actividad real.' : 'Direct agent chat — coming soon. This is their real activity.'}</p>
          )}
        </div>
      </div>
    </div>
  );
}

export default function AiTeamOsPage({ embedded = false }) {
  const { lang } = useT();
  const isEs = lang === 'es';
  const { data: d, err, reload, fetchedAt, loading } = useOs2('/ai-team');
  const [open, setOpen] = useState(null);

  if (err) return <p className="text-sm text-red-600">{err}</p>;
  if (!d) return <LoadingState />;

  const avatars = Object.fromEntries(TEAM_AVATARS.map((a) => [a.key, a.img]));

  return (
    <div data-testid="os2-ai-team">
      {embedded ? (
        <p className="mb-3 text-[12px] text-gray-400">
          {isEs ? '¿Está trabajando cada agente, y qué hace ahora mismo?' : 'Is every agent working, and what is each doing right now?'}
          <span className="ml-2 tabular-nums font-semibold text-gray-500">{d.events_7d_total} {isEs ? 'eventos reales · 7d' : 'real events · 7d'}</span>
        </p>
      ) : (
      <PageHead
        title={isEs ? 'Equipo AI' : 'AI Team'}
        question={isEs ? '¿Está trabajando cada agente AI, y qué hace cada uno ahora mismo?' : 'Is every AI agent working, and what is each one doing right now?'}
        fresh={{ at: fetchedAt, onReload: reload, loading, isEs }}
        module="AI OS"
        right={<span className="text-[12.5px] text-gray-400 tabular-nums">{d.events_7d_total} {isEs ? 'eventos reales · 7d' : 'real events · 7d'}</span>}
      />
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {d.agents.map((a, i) => {
          const working = a.timeline?.[0];
          const active = a.status !== 'idle';
          return (
            <Fade key={a.key} delay={i * 0.07}>
              <Card className="p-5 h-full flex flex-col hover:shadow-md transition-shadow duration-300" data-testid={`aiteam-agent-${a.key}`}>
                <div className="flex items-start gap-3">
                  <div className="relative shrink-0">
                    <img src={avatars[a.key]} alt={a.name} className="w-12 h-12 rounded-xl object-cover border border-gray-200" loading="lazy" />
                    <span className={`absolute -bottom-0.5 -right-0.5 flex w-3.5 h-3.5`}>
                      {active && <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-60" />}
                      <span className={`relative inline-flex rounded-full h-3.5 w-3.5 border-2 border-white ${active ? 'bg-emerald-500' : 'bg-amber-400'}`} />
                    </span>
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-[15px] font-semibold tracking-tight text-gray-900">{a.name}</p>
                    <p className="text-[11px] tracking-wider uppercase text-gray-400 font-medium truncate">{a.role}</p>
                    <p className={`text-[11px] font-semibold mt-0.5 ${active ? 'text-emerald-600' : 'text-amber-600'}`}>
                      ● {active ? (isEs ? 'Activo' : 'Active') : (isEs ? 'En espera' : 'Standby')}
                      <span className="text-gray-300 font-normal tabular-nums"> · {a.events_7d} {isEs ? 'acciones 7d' : 'actions 7d'}</span>
                    </p>
                  </div>
                </div>

                <div className="mt-4 rounded-xl bg-gray-50 border border-gray-100 p-3 space-y-2 flex-1">
                  <div>
                    <p className="text-[10px] tracking-[0.15em] uppercase text-gray-400 font-bold">{isEs ? 'Trabajando en' : 'Working on'}</p>
                    <p className="text-[13px] text-gray-800 capitalize truncate">
                      {working ? `${friendly(working.type)}${working.detail ? ` — ${working.detail}` : ''}` : a.mission}
                    </p>
                    {working && <p className="text-[10.5px] text-gray-400">{timeAgo(working.at, isEs)} {isEs ? 'atrás' : 'ago'}</p>}
                  </div>
                  <div>
                    <p className="text-[10px] tracking-[0.15em] uppercase text-gray-400 font-bold">{isEs ? 'Agenda' : 'Schedule'}</p>
                    <p className="text-[12.5px] text-gray-600">{SCHEDULE[a.key]?.[isEs ? 1 : 0]}</p>
                  </div>
                  {a.warnings_7d > 0 && (
                    <p className="text-[11.5px] text-red-500 font-medium">⚠️ {a.warnings_7d} {isEs ? 'avisos esta semana' : 'warnings this week'}</p>
                  )}
                </div>

                <button onClick={() => setOpen(a)}
                  className="mt-3 w-full inline-flex items-center justify-center gap-1.5 rounded-full border border-gray-200 bg-white hover:bg-gray-900 hover:text-white hover:border-gray-900 text-gray-700 text-[12.5px] font-semibold px-4 py-2 transition-colors"
                  data-testid={`aiteam-open-conversation-${a.key}`}>
                  <MessageCircle className="w-3.5 h-3.5" /> {isEs ? 'Abrir conversación' : 'Open conversation'}
                </button>
              </Card>
            </Fade>
          );
        })}
      </div>
      <p className="mt-4 text-[11.5px] text-gray-400">
        {isEs ? 'KPIs por agente (tareas completadas / SLA): fuente de datos no conectada aún.' : 'Per-agent KPIs (tasks completed / SLA): data source not connected yet.'}
      </p>
      {open && <ConversationModal agent={open} avatar={avatars[open.key]} isEs={isEs} onClose={() => setOpen(null)} />}
    </div>
  );
}
